import classNames from 'classnames';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import { Component, type ReactNode } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router';

import { plural } from '../../../../../shared/helpers/plural';
import { renderPackage, renderRevision } from '../../../../../shared/helpers/renderLocator';
import Locator from '../../../../../shared/Locator';
import { get } from '../../../../redux/api';
import utils from '../../../../utils';
import Icon, { icons } from '../../../generic/Icon';
import Loader from '../../../generic/Loader';
import type { Obligation } from '../../../../../typings/api';
import type { APIError, Dictionary } from '../../../../types';
import type { MinimalRevision, Path, RevisionPaths } from '../../types';

const AUTOLOAD_SOURCES_LIMIT = 7;

// These rules are disabled due to clickable `li` elements.
// Ideally, these would be followed. However we use clickable li elements a lot,
// so this is low priority.
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/anchor-is-valid */

type LicenseSourcePillProps = {
  license: string;
  onClick: (license: string) => void;
};

class LicenseSourcePill extends Component<LicenseSourcePillProps> {
  onClick = () => this.props.onClick(this.props.license);

  render() {
    const { license } = this.props;
    return (
      <a onClick={this.onClick} className="btn-view-summary clickable">
        {license}
      </a>
    );
  }
}

type ObligationsSourcePanelProps = {
  sources: string[];
  revisions: string[];
};

type ObligationsSourcePanelState = {
  loading: boolean;
  expanded: boolean;
  paths: Array<{
    target: MinimalRevision;
    revision: Locator;
    source: Locator;
    path: Path;
  }>;
  error?: APIError;
};

class ObligationsSourcePanel extends Component<ObligationsSourcePanelProps, ObligationsSourcePanelState> {
  readonly state: ObligationsSourcePanelState = {
    loading: false,
    expanded: false,
    paths: [],
  };

  componentDidMount() {
    if (this.props.sources.length < AUTOLOAD_SOURCES_LIMIT) {
      this.loadExpandedData();
    }
  }

  loadExpandedData = async () => {
    this.setState({ expanded: true, loading: true });
    try {
      const combinedPaths = await Promise.all(this.props.revisions.map(async revision => {
        const response = await get<RevisionPaths>(`/api/revisions/${utils.nuc(revision)}/paths`, false, {
          locator: this.props.sources.map(utils.nuc),
          maxResults: 100 * this.props.sources.length,
        });

        return flatten(response.data.map((sourceResults, i) => {
          const source = this.props.sources[i];
          return sourceResults.map(paths => ({
            target: paths[paths.length - 1],
            path: paths.slice(0, -1),
            revision: new Locator(revision),
            source: new Locator(source),
          }));
        }));
      }));

      this.setState({ paths: flatten(compact(combinedPaths)), loading: false });
    } catch (error) {
      /*
      Requests seem to reliably fail if we request too many revisions; in prod if we try to view more than a handful
      of revisions we just infinitely spin.
      This functionality is there to prevent from infinitely spinning; instead we show an error message.
      */
      this.setState({ error: new Error(error.data?.message ?? error?.message ?? error), loading: false });
    }
  };

  render() {
    if (this.state.loading) {
      return <Loader />;
    }

    if (this.state.error) {
      return (
        <div className="collapse-bar" onClick={this.loadExpandedData}>
          Failed to load data: {this.state.error.message}
        </div>
      );
    }

    const count = this.props.sources.length;
    if (!this.state.expanded) {
      return (
        <div className="collapse-bar" onClick={this.loadExpandedData}>
          {count} {plural(count, 'Project')}...
        </div>
      );
    }

    return (
      <ul className="sources-list list list-clean">
        {this.props.sources.map(source => {
          const paths = this.state.paths.filter(p => p.target.locator === source);
          const sourceLocator = new Locator(source);
          const [current] = paths;
          if (!current) {
            return undefined;
          }

          const [startPath] = current?.path || [];
          return (
            <li key={source + current.revision.toString()} className="clickable">
              <div className="source-content">
                <Link to={`/projects/${utils.nuc(current.revision.toPackageString())}/refs/branch/master/${current.revision.revision.value}/browse/dependencies/${utils.nuc(current.target.locator)}`}>
                  {utils.trimString(current.target.title ?? renderPackage(sourceLocator) ?? '', 23)}{' '}
                  <b>({utils.trimString(renderRevision(sourceLocator) ?? '', 8)})</b>
                </Link>
                {paths.length > 1 ? (
                  <div className="dep-label">
                    Depended on in {paths.length >= 99 ? '99+' : paths.length} ways
                  </div>
                ) : (
                  <div className="dep-label" onClick={utils.stopEventPropagation}>
                    {utils.numSuffix(current?.path.length)}-level dep of{' '}
                    <Link to={`/projects/${utils.nuc(current.revision.toPackageString())}/refs/branch/master/${current.revision.revision.value}/browse/dependencies/${utils.nuc(startPath?.locator)}`}>
                      {utils.trimString(startPath?.title || renderPackage(startPath), 18)}
                    </Link>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    );
  }
}

type DirectiveObligationsProps = {
  termId: string;
  directive: string;
  obligations: Obligation[];
  revisions: string[];
  onSelectLicense: (license: string) => void;
};

const DirectiveObligations = (props: DirectiveObligationsProps) => {
  const collated: Dictionary<{licenses: string[]; sources: string[]}> = {};

  props.obligations.forEach(obligation => {
    const collation = collated[obligation.description] ?? { licenses: [], sources: [] };

    collation.licenses.push(obligation.license);
    Object.values(obligation.revisions).forEach(revision => {
      collation.sources.push(...revision);
    });

    collated[obligation.description] = collation;
  });

  Object.keys(collated).forEach(description => {
    const collation = collated[description];
    collation.licenses = uniq(collation.licenses).sort();
    collation.sources = uniq(collation.sources).sort();
    collated[description] = collation;
  });

  return (
    <div>
      {Object.keys(collated).sort().map(description => {
        const { licenses, sources } = collated[description];
        return (
          <div key={description}>
            <OverlayTrigger
              placement="left"
              overlay={
                <Tooltip>
                  <b>Disclaimer:</b> These are summaries sourced from TLDRLegal.
                </Tooltip>
              }
            >
              <div className="attr_text">
                <p>&ldquo;{utils.boldWords(description, props.termId)}&rdquo;</p>
                <div className="attr_source">
                  {licenses.map((license, i) => (
                    <span key={license}>
                      <LicenseSourcePill
                        license={license}
                        onClick={props.onSelectLicense}
                      />{' '}
                      {licenses.length - 1 > i ? '& ' : ''}
                    </span>
                  ))}
                </div>
              </div>
            </OverlayTrigger>
            <ObligationsSourcePanel
              revisions={props.revisions}
              sources={sources}
            />
          </div>
        );
      })}
    </div>
  );
};

type DirectiveItemProps = {
  onSelectDirective: (directive: string) => void;
  onSelectLicense: (license: string) => void;
  revisions: string[];
  directive: string;
  obligations: Obligation[];
  expanded: boolean;
  children?: ReactNode;
};

class DirectiveItem extends Component<DirectiveItemProps> {
  onSelect = () => this.props.onSelectDirective(this.props.directive);

  render() {
    const title = this.props.directive;
    const count = this.props.obligations.reduce((total, obligation) => {
      const subtotal = Object.values(obligation.revisions).reduce((sub, rev) => sub + rev.length, 0);
      return total + subtotal;
    }, 0);

    return (
      <li className={classNames({ active: this.props.expanded })}>
        <div className="attr-head" onClick={this.onSelect}>
          <span className="margin-right-xs">
            <Icon icon={icons.caretRight} />
          </span>
          <span>
            <b>{title}</b> for <b>{count}</b> {plural(count, 'project')}
          </span>
        </div>
        {this.props.expanded && (
          <DirectiveObligations
            termId={title}
            onSelectLicense={this.props.onSelectLicense}
            directive={this.props.directive}
            obligations={this.props.obligations}
            revisions={this.props.revisions}
          />
        )}
      </li>
    );
  }
}

export type ObligationsPanelProps = {
  // revisions is the list of project revisions for which this panel is displayed
  revisions: string[];

  // key is obligation directive (i.e. "Include Copyright"), value is obligations under that directive
  obligations: Dictionary<Obligation[]>;

  selectedLicense: string;
  onSelectLicense: (license: string) => void;

  // Obligation is selected by directive
  selectedObligation: string;
  onSelectObligation: (directive: string) => void;
};

type ObligationsPanelState = {
  expanded: boolean;
};

export class ObligationsPanel extends Component<ObligationsPanelProps, ObligationsPanelState> {
  readonly state = {
    expanded: true,
  };

  toggleExpand = () => {
    this.setState(state => ({ expanded: !state.expanded }));
  };

  render() {
    const directives = Object.keys(this.props.obligations);
    const obligationCount = directives.length;

    return (
      <ul className="list-obligations bucket-list blue">
        <li className="subheader clickable" onClick={this.toggleExpand}>
          <OverlayTrigger overlay={(
            <Tooltip>Not all obligations may apply - check licenses for specific cases.</Tooltip>
          )}>
            <span>
              {!this.state.expanded && <Icon icon={icons.caretRight} />}{' '}
              {obligationCount} {plural(obligationCount, 'Obligation')} Found
              {!this.state.expanded && <Icon icon={icons.caretLeft} />}
            </span>
          </OverlayTrigger>
        </li>
        { this.state.expanded && (
          directives.length ? (
            directives.sort().map(directive => (
              <DirectiveItem
                key={directive}
                directive={directive}
                revisions={this.props.revisions}
                obligations={this.props.obligations[directive]}
                expanded={this.props.selectedObligation === directive}
                onSelectDirective={this.props.onSelectObligation}
                onSelectLicense={this.props.onSelectLicense}
              />
            ))
          ) : (
            <li>
              <div className="attr-head">None Yet.</div>
            </li>
          )
        )}
      </ul>
    );
  }
}
